import { FetchRolloutMonitoringSuccessAction } from './rolloutMonitoring';
import { FetchReleaseStatsHistorySuccessAction } from './releaseStatsHistory';
import {
    BucketMember,
    BucketRule,
    BuildDistroMemberPermissionLevel,
    FeatureFlag,
    FetchBuildDistributionBucketBuildResponse,
    FetchBuildDistributionBucketBuildsResponse,
    FetchBuildDistributionBucketBuildsWorkItemsTimelineResponse,
    FetchBuildDistributionBucketResponse,
    FetchBuildDistributionBucketsWithRecentBuildsResponse,
    FetchBuildDistributionSharedWithMeBuildsResponse,
    GetAllDevicesResponse,
    GetProvisioningProfilesResult,
    OrgOverviewV2,
    ReleaseEntity,
    ReleaseStatisticsHistory,
    ReleaseStep,
    ReleaseStepType,
    RolloutMonitoring,
    UsersAndGroups,
} from './../types';
import { FetchOrgOverviewV2SuccessAction } from './orgOverview';
import { FetchableEntityType, FetchableEntityTypeV2, FetchablePaginatedEntityType } from '../entityType';
import { AppEntity, AppHome, OrganizationEntity, Release } from '../types';
import { FetchAppHomeSuccessAction } from './appHome';
import { ConnectedReleasesResponse, FetchReleasesSuccessAction, FetchReleaseSuccessAction } from './release';
import {
    FetchTimelineEventsHistorySuccessAction,
    TimelineEventsHistoryPaginatedResponse,
} from './timelineEventsHistory';
import { FetchFeatureFlagsSuccessAction } from './featureFlags';
import {
    UpdateAllDevicesPaginationAction,
    UpdateBucketBuildsPaginationAction,
    UpdateBucketBuildWorkItemsTimelinePaginationAction,
    UpdateReleasesPaginationAction,
    UpdateSharedWithMeBuildsPaginationAction,
} from './ui';
import {
    FetchBuildDistributionBucketBuildsSuccessAction,
    FetchBuildDistributionBucketBuildSuccessAction,
    FetchBuildDistributionBucketDownloadMetricsSuccessAction,
    FetchBuildDistributionBucketsSuccessAction,
    FetchBuildDistributionBucketSuccessAction,
    FetchBuildDistributionBuildWorkItemsTimelineSuccessAction,
    FetchBuildDistributionSharedWithMeBuildsSuccessAction,
} from './buildDistribution';
import { UpdateAutomationSettingsRequest } from './appSettings';
import { FetchAllDevicesSuccessAction, FetchProvisioningProfilesSuccessAction } from '../reducers/apps';
import { FetchReleaseStepSuccessAction } from '@/actions/releaseStep';
import { AppStateResponse, FetchAppStateSuccessAction } from './app';
import { FetchReleaseOverviewSuccessAction } from '@/actions/releaseOverview';
import { SummaryViewProperty } from '@/types/summaryView';
import { PaginationBaseRequest } from './pagination';

export interface OrgEntityRequest {
    orgId: string;
}

export interface AppEntityRequest extends OrgEntityRequest {
    appId: string;
}

export interface ReleaseEntityRequest extends AppEntityRequest {
    releaseId: string;
}

export interface ReleaseStepEntityRequest extends ReleaseEntityRequest {
    releaseStepType: ReleaseStepType;
}

export interface PingAllPendingWorkOwnersRequest extends ReleaseEntityRequest {
    shouldPingAllWorkOwners: boolean;
    itemsToPingOwnersOf?: string[];
}

export interface TimelineEventsHistoryEntityRequest extends ReleaseEntityRequest, PaginationBaseRequest {
    releaseStepType: ReleaseStepType;
}

export interface WorkflowEntityRequest extends OrgEntityRequest {
    workflowId: string;
}

export interface WorkflowOverviewEntityRequest extends WorkflowEntityRequest {
    destinationStepIds: string[];
}

export interface OrgSummaryViewEntityRequest extends OrgEntityRequest {
    versions: string[];
    destinationIds: string[];
    properties: SummaryViewProperty[];
}

export interface WorkflowRunEntityRequest extends WorkflowEntityRequest {
    workflowRunId: string;
}

export interface TimelineEventsEntityRequest extends WorkflowRunEntityRequest, PaginationBaseRequest {
    stepRunIds?: string[];
}

export interface StepConfigEntityRequest extends WorkflowEntityRequest {
    stepConfigId: string;
}

export interface AutomationEntityRequest extends WorkflowEntityRequest {
    automationId: string;
}

export interface ChecklistItemTemplateEntityRequest extends StepConfigEntityRequest {
    checklistItemId: string;
}

export interface StepRunEntityRequest extends WorkflowRunEntityRequest {
    stepRunId: string;
}
export interface StepRunsSummaryEntityRequest extends WorkflowRunEntityRequest {
    stepRunIds: string[];
}
export interface StepRunChecklistItemEntityRequest extends StepRunEntityRequest {
    checklistItemId: string;
}

export interface BucketEntityRequest extends AppEntityRequest {
    bucketId: string;
}

export interface BucketMemberEntityRequest extends BucketEntityRequest {
    memberId: string;
}

export interface OrgOverviewFilters {
    orgId?: string;
    appGroupId?: string;
    appId?: string;
    startDate?: string;
    endDate?: string;
}

export interface OrgOverviewEntityRequest extends OrgEntityRequest {
    filters?: OrgOverviewFilters;
    appIds: string[]; //resulting appIds based on filters
}

export interface BuildDistributionBucketEntityRequest extends AppEntityRequest {
    bucketId: string;
}

export interface BuildDistributionBucketBuildEntityRequest extends AppEntityRequest {
    bucketId: string;
    buildId: string;
}

export interface BuildDistributionBucketBuildsEntityRequest extends AppEntityRequest {
    bucketId: string;
    limit?: number;
    offset?: number;
}

export interface BuildDistributionBucketDownloadMetricRequest extends AppEntityRequest {
    lowerBound: string;
    upperBound: string;
    bucketId: string;
}

export type GetProvisioningProfilesRequest = AppEntityRequest;

export interface GetAllDevicesRequest extends AppEntityRequest {
    limit?: number;
    offset?: number;
}

export interface BuildDistributionBucketBuildsWorkItemsTimelineRequest extends AppEntityRequest {
    bucketId: string;
    buildId?: string;
    limit?: number;
    offset?: number;
}

export interface BuildDistributionSharedWithMeBuildsEntityRequest extends AppEntityRequest {
    limit: number;
    offset: number;
}

export interface BuildDistributionBucketsWithRecentBuildsEntityRequest extends AppEntityRequest {
    bucketId: string;
}

export interface BuildDistributionSearchBuildsEntityRequest extends AppEntityRequest {
    query: string;
    bucketId?: string;
}

export interface CreateBuildDistributionBucketRequest extends AppEntityRequest {
    name: string;
    orgWideAccessEnabled: boolean;
    publicBucketLinkEnabled: boolean;
    rules: BucketRule[];
    members: BucketMember[];
}

export interface UpdateBuildDistributionBucketRequest extends AppEntityRequest {
    bucketId: string;
    name: string;
    rules: BucketRule[];
    orgWideAccessEnabled: boolean;
    publicBucketLinkEnabled: boolean;
}

export interface UpdateBuildDistributionBuildTesterNotesRequest extends AppEntityRequest {
    buildId: string;
    bucketId: string;
    testerNotes: string;
}

export interface ArchiveBuildDistributionBuildRequest extends AppEntityRequest {
    buildId: string;
    bucketId: string;
}

export interface ArchiveBuildDistributionBucketRequest extends AppEntityRequest {
    bucketId: string;
}

export interface UpdateBuildDistroBucketNotificationSettingsRequest extends AppEntityRequest {
    bucketId: string;
    notificationId: string;
    enabled: boolean;
    channels: string[];
}

export interface UpdateBuildDistroBucketAutomationSettingsRequest extends UpdateAutomationSettingsRequest {
    bucketId: string;
}

export interface ToggleBuildDistroBucketNotificationsRequest extends AppEntityRequest {
    enabled: boolean;
    bucketId: string;
}

export interface UpdateBucketTestersRequest extends AppEntityRequest {
    testers: UsersAndGroups[];
}

export interface ShareBuildDistributionBuildWithTestersRequest extends BucketEntityRequest {
    buildId: string;
    testers: string[];
}

// export interface RemoveBuildDistributionBuildTestersRequest extends BucketEntityRequest {
//     buildId: string;
//     tester: string;
// }

export interface AddBuildDistroBucketMembersRequest extends BucketEntityRequest {
    members: BucketMember[];
}

export type RemoveBuildDistroBucketMemberRequest = BucketMemberEntityRequest;

export interface UpdateBuildDistroBucketMemberPermissionsRequest extends BucketMemberEntityRequest {
    permissionLevel: BuildDistroMemberPermissionLevel;
}

export type FeatureFlagsResponse = FeatureFlag[];

export type EntityRequest =
    | OrgEntityRequest
    | AppEntityRequest
    | ReleaseEntityRequest
    | TimelineEventsHistoryEntityRequest
    | ReleaseStepEntityRequest
    | BuildDistributionBucketsWithRecentBuildsEntityRequest
    | BuildDistributionBucketEntityRequest
    | BuildDistributionBucketBuildEntityRequest
    | GetProvisioningProfilesRequest
    | OrgOverviewEntityRequest
    | ReleasesRequest;

export type EntityResponse =
    | AppHome
    | AppStateResponse
    | Release
    | TimelineEventsHistoryPaginatedResponse
    | ReleaseStatisticsHistory
    | RolloutMonitoring
    | AppEntity
    | OrganizationEntity
    | OrgOverviewV2
    | FetchBuildDistributionBucketsWithRecentBuildsResponse
    | FetchBuildDistributionBucketResponse
    | FetchBuildDistributionBucketBuildResponse
    | FetchBuildDistributionSharedWithMeBuildsResponse
    | GetProvisioningProfilesResult
    | ReleaseStep
    | FeatureFlagsResponse;

export type FetchEntitySuccessAction =
    | FetchAppStateSuccessAction
    | FetchReleaseSuccessAction
    | FetchTimelineEventsHistorySuccessAction
    | FetchRolloutMonitoringSuccessAction
    | FetchFeatureFlagsSuccessAction
    | FetchReleaseStatsHistorySuccessAction
    | FetchAppHomeSuccessAction
    | FetchBuildDistributionBucketsSuccessAction
    | FetchBuildDistributionBucketSuccessAction
    | FetchBuildDistributionBucketBuildsSuccessAction
    | FetchBuildDistributionBucketBuildSuccessAction
    | FetchBuildDistributionSharedWithMeBuildsSuccessAction
    | FetchReleaseOverviewSuccessAction
    | FetchProvisioningProfilesSuccessAction
    | FetchReleaseStepSuccessAction
    | FetchOrgOverviewV2SuccessAction
    | FetchBuildDistributionBucketDownloadMetricsSuccessAction;

export interface FetchEntityRequest {
    id?: string | number;
    data: EntityRequest;
}

export interface TryFetchEntityAction {
    type: FetchableEntityType;
    payload: FetchEntityRequest;
}

export interface CommonSagaActionsData {
    type: string;
    payload: {
        id: string;
    };
    paginationData?: {
        limit: number;
        offset: number;
    };
}

export interface TryFetchEntityActionV2<T extends FetchableEntityTypeV2 = FetchableEntityTypeV2>
    extends CommonSagaActionsData {
    type: T;
    payload: {
        id: string;
        data: TryFetchEntityActionData<T>;
    };
}

export type TryFetchEntityActionData<T extends FetchableEntityTypeV2> =
    T extends FetchableEntityTypeV2.Workflow ? WorkflowEntityRequest
    : T extends FetchableEntityTypeV2.WorkflowRun ? WorkflowRunEntityRequest
    : T extends FetchableEntityTypeV2.StepRun ? StepRunEntityRequest
    : T extends FetchableEntityTypeV2.StepRunsSummary ? StepRunsSummaryEntityRequest
    : T extends FetchableEntityTypeV2.StepConfig ? StepConfigEntityRequest
    : T extends FetchableEntityTypeV2.WorkflowOverview ? WorkflowOverviewEntityRequest
    : T extends FetchableEntityTypeV2.OrgChecklistItemTemplates ? OrgEntityRequest
    : never;

export const tryFetchEntity: (
    type: FetchableEntityType,
    data: EntityRequest,
    id?: string | number,
) => TryFetchEntityAction = (type, data, id) => {
    return {
        type,
        payload: {
            id,
            data,
        },
    };
};

export const tryFetchEntityV2: <T extends FetchableEntityTypeV2>(
    type: T,
    data: TryFetchEntityActionData<T>,
    id: string,
    paginationData?: CommonSagaActionsData['paginationData'],
) => TryFetchEntityActionV2<T> = (type, data, id, paginationData) => {
    return {
        type,
        payload: {
            id,
            data,
        },
        paginationData,
    };
};

// Paginated Entities

export type ReleasesResponse = ReleaseEntity[];

export interface ReleasesRequest extends AppEntityRequest {
    orgIdParam: string;
    appIdParam: string;
}

export type PaginatedEntitiesRequest =
    | ReleasesRequest
    | GetAllDevicesRequest
    | BuildDistributionBucketBuildsEntityRequest
    | BuildDistributionBucketBuildsWorkItemsTimelineRequest
    | BuildDistributionSharedWithMeBuildsEntityRequest;

export type PaginatedEntitiesResponse =
    | ReleasesResponse
    | GetAllDevicesResponse
    | FetchBuildDistributionBucketBuildsResponse
    | FetchBuildDistributionBucketBuildsWorkItemsTimelineResponse
    | FetchBuildDistributionSharedWithMeBuildsResponse;

export type FetchPaginatedEntitiesSuccessAction =
    | FetchReleasesSuccessAction
    | FetchAllDevicesSuccessAction
    | FetchBuildDistributionBucketBuildsSuccessAction
    | FetchBuildDistributionBuildWorkItemsTimelineSuccessAction
    | FetchBuildDistributionSharedWithMeBuildsSuccessAction;

export type FetchPaginatedEntitiesUpdatePaginationDataAction =
    | UpdateAllDevicesPaginationAction
    | UpdateReleasesPaginationAction
    | UpdateBucketBuildsPaginationAction
    | UpdateBucketBuildWorkItemsTimelinePaginationAction
    | UpdateSharedWithMeBuildsPaginationAction;

export type FetchPaginatedEntitiesSuccessPayloadtype = ConnectedReleasesResponse;

export interface FetchPaginatedEntitiesRequest {
    id?: string | number;
    limit: number;
    offset: number;
    data: PaginatedEntitiesRequest;
}

export interface TryFetchPaginatedEntitiesAction {
    type: FetchablePaginatedEntityType;
    payload: FetchPaginatedEntitiesRequest;
}

export const tryFetchPaginatedEntities: (
    type: FetchablePaginatedEntityType,
    data: PaginatedEntitiesRequest,
    limit: number,
    offset: number,
    id?: string | number,
) => TryFetchPaginatedEntitiesAction = (type, data, limit, offset, id) => {
    return {
        type,
        payload: {
            id,
            limit,
            offset,
            data,
        },
    };
};
